import { BottomSheetTextInput } from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { showErrorToast } from "@-/dev.improve.haniverse.components.ui.failure-toast";
import { getAuthClient } from "@-/dev.improve.haniverse.lib.auth.client";
import { getOrpcClient } from "@-/dev.improve.haniverse.lib.orpc";
import { toast } from "@-/com.npmjs.sonner";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { withUniwind } from "@-/com.npmjs.uniwind";
import { useState } from "react";
import { View } from "@-/com.npmjs.react-native";

const UniwindBottomSheetTextInput = withUniwind(BottomSheetTextInput);

const INPUT_CLASS_NAME =
  "min-h-36 w-full min-w-0 self-stretch rounded-xs border border-input bg-background px-3 py-3 font-sans text-base text-foreground dark:bg-input/30";

/**
 * In-sheet form that emails Leon via Resend. Replaces the old Sentry feedback widget, which
 * never landed in an inbox and was disabled in local builds.
 */
export function SupportFeedbackForm({ onCancel }: { onCancel: () => void }) {
  const { data: session } = getAuthClient().useSession();
  const [feedback, setFeedback] = useState("");
  const feedbackMutation = useMutation({
    mutationFn: async (submittedFeedback: string) =>
      getOrpcClient.feedback({
        feedback: submittedFeedback,
        replyEmail: session?.user.email ?? "",
      }),
    mutationKey: ["feedback", "app"],
  });

  const submit = () => {
    const trimmedFeedback = feedback.trim();
    if (trimmedFeedback.length === 0 || feedbackMutation.isPending) {
      return;
    }

    feedbackMutation.mutate(trimmedFeedback, {
      onError: (error) => {
        showErrorToast("Could not send feedback", error);
      },
      onSuccess: () => {
        toast.success("Feedback sent — thank you!");
        onCancel();
      },
    });
  };

  return (
    <View className="gap-3" testID="support-feedback-form">
      <Text className="text-muted-foreground" variant="p">
        Bugs, confusing bits, ideas, or questions — this message goes straight to Leon&apos;s email.
      </Text>
      <UniwindBottomSheetTextInput
        accessibilityLabel="Feedback message"
        autoFocus
        className={INPUT_CLASS_NAME}
        maxLength={4000}
        multiline
        onChangeText={setFeedback}
        placeholder="Write your feedback…"
        testID="support-feedback-input"
        textAlignVertical="top"
        value={feedback}
      />
      <View className="flex-row justify-end gap-2">
        <Button accessibilityLabel="Cancel feedback" onPress={onCancel} variant="outline">
          <Text>Cancel</Text>
        </Button>
        <Button
          accessibilityLabel="Send feedback"
          disabled={feedback.trim().length === 0 || feedbackMutation.isPending}
          isLoading={feedbackMutation.isPending}
          onPress={submit}
          testID="support-feedback-submit"
        >
          <Text>Send</Text>
        </Button>
      </View>
    </View>
  );
}
